import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Slider, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { t } from 'i18next';
import { fetchUser, getUserGroups } from '../../../services/users';
import { getPatientPlans } from '../../../services/patient';
import { fetchWeeklyComputations } from '../../../services/computations';
import { usePatientQuestionnaireAnswers } from '../../../hooks/usePatientQuestionnaireAnswers';
import { usePatientPendingAnswers } from '../../../hooks/usePatientPendingAnswers';

import { SelectionButton } from '../../../component/buttons';
import ConsultationCard from '../Consultation/ConsultationCard';
import ScheduleConsultationDialog from '../Consultation/ScheduleConsultationDialog';
import { fetchAppointments } from '../../../services/appointments';
import AddMedication from '../medication';
import PersonalData from './personalData';
import Records from './records';
import UserStats from './components/UserStats';
import WeeklyProgressChart from './components/WeeklyProgressChart';
import ExerciseRatioPie from './components/ExerciseRatioPie';
import ExerciseTypesPie from './components/ExerciseTypesPie';
import PendingQuestionnaires from './components/PendingQuestionnaires';
import AnsweredQuestionnaires from './components/AnsweredQuestionnaires';
import HealthStats from './components/HealthStats';
import IpaqScoresChart from './components/IpaqScoresChart';
import PatientPlanTimeline from './components/PatientPlanTimeline';
import PatientActivePlan from './components/PatientActivePlan';
import CardiovascularHealthSection from './components/CardiovascularHealthSection';
import StressTestsSection from './components/StressTestsSection';

const SeeUser = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const restoredState = state?.restoredState;

    useEffect(() => {
        if (restoredState) {
            if (restoredState.selected !== undefined) setSelected(restoredState.selected);
            if (restoredState.expanded !== undefined) setExpanded(restoredState.expanded);
            if (restoredState.selectedPlan) setSelectedPlan(restoredState.selectedPlan);
        }
    }, [restoredState]);

    const { user } = state || {};
    const roleUuid = user?.role_uuid || '';

    const [groups, setGroups] = useState([]);
    const [userData, setUserData] = useState([]);
    const [plans, setPlans] = useState([]);
    const [weeklyDataList, setWeeklyDataList] = useState([]);
    const [selected, setSelected] = useState(0);

    const [loadingAppointments, setLoadingAppointments] = useState(false);

    const [scheduledConsultations, setScheduledConsultations] = useState([]);
    const [completedConsultations, setCompletedConsultations] = useState([]);

    const sections = [
        t('userPage.sections.general'),
        t('userPage.sections.appointment'),
        t('userPage.sections.exams'),
        t('userPage.sections.medication'),
        t('userPage.sections.activity'),
        t('userPage.sections.questionnaires')
    ];
    const [expanded, setExpanded] = useState(true);

    const [selectedPlan, setSelectedPlan] = useState(null);
    const selectedPlanUuid = selectedPlan?.uuid || null;
    const selectedPlanStart = selectedPlan?.start_date || null;
    const selectedPlanEnd = selectedPlan?.end_date || null;
    const [numWeeks, setNumWeeks] = useState(4);

    const totalExercises = weeklyDataList.reduce(
        (sum, week) =>
            sum +
            Object.values(week.data.exercises?.done_in_plan || {}).reduce(
                (a, b) => a + b,
                0
            ),
        0
    );

    const totalSkipped = weeklyDataList.reduce(
        (sum, week) =>
            sum +
            Object.values(week.data.exercises?.pending_in_plan || {}).reduce(
                (a, b) => a + b,
                0
            ),
        0
    );

    const aggregatedTypes = {};
    weeklyDataList.forEach((week) => {
        const total = week.data.exercises?.total || {};
        Object.entries(total).forEach(([type, count]) => {
            if (!aggregatedTypes[type]) aggregatedTypes[type] = 0;
            aggregatedTypes[type] += count;
        });
    });
    const exerciseTypes = Object.entries(aggregatedTypes).map(([name, value]) => ({
        name,
        value
    }));

    const weeklyStats = weeklyDataList.map((week) => {
        const done = Object.values(week.data.exercises?.done_in_plan || {}).reduce(
            (a, b) => a + b,
            0
        );
        const skipped = Object.values(week.data.exercises?.pending_in_plan || {}).reduce(
            (a, b) => a + b,
            0
        );
        return { week: week.week, done, skipped };
    });

    const ratio =
        totalSkipped === 0
            ? '0'
            : `${Math.round((totalExercises / (totalExercises + totalSkipped)) * 100)}%`;

    const {
        data: questionnaireAnswers = [],
        isLoading: qaLoading,
        count: qaTotalCount,
        paginationModel: qaPaginationModel,
        setPaginationModel: setQaPaginationModel,
        rowsPerPageOptions: qaRowsPerPageOptions
    } = usePatientQuestionnaireAnswers(roleUuid);

    const {
        data: pendingAnswers = [],
        count: pendingTotalCount = 0,
        paginationModel: pendingPaginationModel = { page: 0, pageSize: 10 },
        setPaginationModel: setPendingPaginationModel = () => {}
    } = usePatientPendingAnswers(roleUuid);

    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

    const seeUserState = React.useMemo(
        () => ({
            selected,
            expanded,
            selectedPlan
        }),
        [selected, expanded, selectedPlan]
    );

    const renderConsultationGrid = (consultations, onCardClick) => (
        <Grid container spacing={2}>
            {consultations.map((consult) => (
                <Grid item xs={12} md={6} lg={4} key={consult.uuid}>
                    <ConsultationCard
                        appointment={consult}
                        onClick={() => onCardClick(consult)}
                    />
                </Grid>
            ))}
        </Grid>
    );

    useEffect(() => {
        if (!roleUuid) return;
        const fetchData = async () => {
            try {
                const [userResp, groupsResp, plansResp] = await Promise.all([
                    fetchUser(roleUuid),
                    getUserGroups(roleUuid),
                    getPatientPlans(roleUuid)
                ]);
                setUserData(userResp);
                setGroups(groupsResp);
                setPlans(plansResp);
            } catch (error) {
                console.error('Erro ao procurar dados do paciente:', error);
            }
        };
        fetchData();
    }, [roleUuid]);

    useEffect(() => {
        const fetchWeeklyData = async () => {
            if (!user?.role_uuid) return;

            try {
                let weeks = [];

                if (selectedPlan) {
                    const start = dayjs(selectedPlan.start_date);
                    const end = dayjs(selectedPlan.end_date);

                    const numWeeks = Math.ceil(end.diff(start, 'week', true));
                    weeks = [...Array(numWeeks)].map((_, i) =>
                        start.add(i, 'week').startOf('week').format('YYYY-MM-DD')
                    );
                } else {
                    const today = dayjs();
                    weeks = [...Array(numWeeks)]
                        .map((_, i) =>
                            today.subtract(i, 'week').startOf('week').format('YYYY-MM-DD')
                        )
                        .reverse();
                }

                const results = await Promise.all(
                    weeks.map((weekStart) =>
                        fetchWeeklyComputations({
                            qi: weekStart,
                            patient: user.role_uuid,
                            plan: selectedPlanUuid || undefined
                        }).then((data) => ({
                            week: dayjs(weekStart).format('DD-MM'),
                            data
                        }))
                    )
                );

                setWeeklyDataList(results);
            } catch (err) {
                console.error('Erro ao procurar dados das semanas:', err);
            }
        };

        fetchWeeklyData();
    }, [user?.role_uuid, selectedPlanUuid, numWeeks]);

    useEffect(() => {
        if (!user?.role_uuid) return;

        const loadAppointments = async () => {
            setLoadingAppointments(true);
            try {
                const scheduled = await fetchAppointments({
                    patient: user.role_uuid,
                    only_confirmed: true,
                    only_upcoming: true
                });

                const completed = await fetchAppointments({
                    patient: user.role_uuid,
                    only_confirmed: true,
                    only_upcoming: false
                });

                setScheduledConsultations(scheduled.filter((a) => !a.completed));

                setCompletedConsultations(completed.filter((a) => a.completed));
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingAppointments(false);
            }
        };

        loadAppointments();
    }, [user?.role_uuid]);

    if (!user?.role_uuid) {
        return (
            <Box p={2}>
                <Typography color="error">{t('userPage.invalidUser')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                    <PersonalData
                        user={userData.data || {}}
                        expanded={expanded}
                        onToggle={() => setExpanded((prev) => !prev)}
                    />
                </CardContent>
            </Card>

            <Grid container spacing={2} columns={6} sx={{ mb: 3 }}>
                {sections.map((section, index) => (
                    <Grid item key={index} xs={1}>
                        <SelectionButton
                            selected={selected === index}
                            sx={{ width: '100%', py: 2 }}
                            onClick={() => {
                                setSelected(index);
                                if (index === 0) {
                                    setExpanded(true);
                                } else {
                                    setExpanded(false);
                                }
                            }}
                        >
                            {section}
                        </SelectionButton>
                    </Grid>
                ))}
            </Grid>
            {selected === 0 && (
                <>
                    <Card sx={{ mb: 2 }}>
                        <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                {t('userPage.healthData')}
                            </Typography>
                            <Box
                                sx={{
                                    height: 3,
                                    width: 120,
                                    backgroundColor: '#1976d2',
                                    borderRadius: 2,
                                    mb: 2
                                }}
                            />
                            <HealthStats patient={user.role_uuid} />
                        </CardContent>
                    </Card>
                    <PatientActivePlan plans={plans} groups={groups} />
                </>
            )}
            {selected === 1 && (
                <>
                    <Box sx={{ mt: 2 }}>
                        <Card>
                            <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                                {/* header */}
                                <Box display="flex" justifyContent="space-between" mb={2}>
                                    <Typography variant="h5" fontWeight={700}>
                                        {t('userPage.completed_consultations')}
                                    </Typography>
                                </Box>

                                {completedConsultations.length === 0 && (
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t('userPage.noConsultation')}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Realizadas */}
                                {completedConsultations.length > 0 &&
                                    renderConsultationGrid(
                                        completedConsultations,
                                        (consult) =>
                                            navigate(
                                                `/users/user/consultation/${consult.uuid}`
                                            )
                                    )}
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Card sx={{ mb: 3 }}>
                            <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                                {/* header */}
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="h5" fontWeight={700}>
                                        {t('userPage.scheduled_consultations')}
                                    </Typography>
                                    <Box display="flex" gap={2}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => setScheduleDialogOpen(true)}
                                        >
                                            {t('userPage.create_consultation')}
                                        </Button>
                                    </Box>
                                </Box>

                                {scheduledConsultations.length === 0 && (
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t('userPage.noScheduleConsultation')}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Agendadas */}
                                {scheduledConsultations.length > 0 &&
                                    renderConsultationGrid(
                                        scheduledConsultations,
                                        (consult) =>
                                            navigate('/users/user/consultation/create', {
                                                state: {
                                                    patient: userData.data,
                                                    appointmentUuid: consult.uuid,
                                                    from: {
                                                        pathname: location.pathname,
                                                        search: location.search
                                                    },
                                                    seeUserState
                                                }
                                            })
                                    )}
                            </CardContent>
                        </Card>
                    </Box>

                    <ScheduleConsultationDialog
                        open={scheduleDialogOpen}
                        onClose={() => setScheduleDialogOpen(false)}
                        patient={user}
                    />
                </>
            )}
            {selected === 2 && (
                <StressTestsSection user={userData.data} seeUserState={seeUserState} />
            )}

            {selected === 3 && <AddMedication patientUUID={userData.data?.role_uuid} />}

            {selected === 4 && (
                <>
                    <Card sx={{ mb: 2 }}>
                        <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                {t('userPage.generalData')}
                            </Typography>
                            <Box
                                sx={{
                                    height: 3,
                                    width: 95,
                                    backgroundColor: '#1976d2',
                                    borderRadius: 2,
                                    mb: 2
                                }}
                            />
                            <UserStats
                                groups={groups}
                                plans={plans}
                                totalExercises={totalExercises}
                                totalSkipped={totalSkipped}
                                onSelectPlan={setSelectedPlan}
                                selectedPlan={selectedPlan}
                            />
                        </CardContent>
                    </Card>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <WeeklyProgressChart weeklyStats={weeklyStats} />
                            {!selectedPlanUuid && (
                                <Box sx={{ ml: 3, px: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {t('userPage.weeksToView', { numWeeks })}
                                    </Typography>
                                    <Slider
                                        value={numWeeks}
                                        onChangeCommitted={(_, newValue) =>
                                            setNumWeeks(newValue)
                                        }
                                        min={4}
                                        max={52}
                                        step={1}
                                        marks={[
                                            { value: 4, label: '4' },
                                            { value: 12, label: '12' },
                                            { value: 26, label: '26' },
                                            { value: 52, label: '52' }
                                        ]}
                                        valueLabelDisplay="auto"
                                        sx={{ width: '100%', mx: 'auto' }}
                                    />
                                </Box>
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <ExerciseRatioPie
                                        totalExercises={totalExercises}
                                        totalSkipped={totalSkipped}
                                        ratio={ratio}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ExerciseTypesPie exerciseTypes={exerciseTypes} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    {/**{selectedPlan && (
                        <CardiovascularHealthSection
                            planStart="2023-09-29"
                            planEnd="2023-12-15"
                        />
                    )}**/}
                    {selectedPlan && (
                        <PatientPlanTimeline
                            patient={user.role_uuid}
                            planUuid={selectedPlanUuid}
                            startData={selectedPlanStart}
                            endData={selectedPlanEnd}
                            selected={selected}
                            expanded={expanded}
                            selectedPlan={selectedPlan}
                        />
                    )}
                    <Card>
                        <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                            <Typography variant="h5" fontWeight={700}>
                                {t('userPage.exercises')}
                            </Typography>
                            <Box
                                sx={{
                                    height: 3,
                                    width: 75,
                                    backgroundColor: '#1976d2',
                                    borderRadius: 2,
                                    mb: 2
                                }}
                            />
                            <Records
                                patient={user.role_uuid}
                                planUuid={selectedPlanUuid}
                                seeUserState={seeUserState}
                            />
                        </CardContent>
                    </Card>
                </>
            )}

            {selected === 5 && (
                <>
                    {/**<IpaqScoresChart />**/}
                    <PendingQuestionnaires
                        pendingAnswers={pendingAnswers}
                        pendingTotalCount={pendingTotalCount}
                        pendingPaginationModel={pendingPaginationModel}
                        setPendingPaginationModel={setPendingPaginationModel}
                    />
                    <AnsweredQuestionnaires
                        questionnaireAnswers={questionnaireAnswers}
                        qaLoading={qaLoading}
                        qaTotalCount={qaTotalCount}
                        qaPaginationModel={qaPaginationModel}
                        setQaPaginationModel={setQaPaginationModel}
                        qaRowsPerPageOptions={qaRowsPerPageOptions}
                    />
                </>
            )}
        </Box>
    );
};

export default SeeUser;
